import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { FileEdit, FilePlus, Sparkles } from 'lucide-react'

interface OverwriteAppendDialogProps {
    isOpen: boolean
    onOpenChange: (open: boolean) => void
    onChoice: (choice: 'overwrite' | 'append') => void
}

export const OverwriteAppendDialog = ({
    isOpen,
    onOpenChange,
    onChoice,
}: OverwriteAppendDialogProps) => {
    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px] border-border bg-background p-0 overflow-hidden shadow-2xl rounded-xl">
                <div className="flex flex-col">
                    {/* Header Section */}
                    <div className="bg-gradient-to-bx from-primary/10 via-primary/5 to-transparent p-6 border-b">
                        <DialogHeader>
                            <div className="flex items-center gap-2 mb-2">
                                <div className="p-2 bg-primary/10 rounded-full flex items-center justify-center">
                                    <Sparkles className="w-5 h-5 text-primary" />
                                </div>
                                <DialogTitle className="text-xl">Generate Outline</DialogTitle>
                            </div>
                            <DialogDescription className="text-base text-muted-foreground">
                                How would you like to add the generated outline to your screenplay?
                            </DialogDescription>
                        </DialogHeader>
                    </div>

                    {/* Content Section */}
                    <div className="p-6">
                        <div className="grid grid-cols-2 gap-4">
                            <button
                                className="group relative flex flex-col items-center justify-center p-6 gap-4 rounded-xl border-2 border-muted bg-transparent hover:border-primary hover:bg-primary/5 transition-all outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                onClick={() => {
                                    onChoice('overwrite')
                                    onOpenChange(false)
                                }}
                            >
                                <div className="p-3 bg-muted group-hover:bg-primary/10 rounded-full transition-colors">
                                    <FileEdit className="h-8 w-8 text-foreground group-hover:text-primary transition-colors" />
                                </div>
                                <div className="space-y-1 text-center">
                                    <span className="block font-semibold text-foreground">Overwrite</span>
                                    <span className="block text-xs text-muted-foreground w-full px-2">
                                        Replace current content with the new outline
                                    </span>
                                </div>
                            </button>
                            <button
                                className="group relative flex flex-col items-center justify-center p-6 gap-4 rounded-xl border-2 border-muted bg-transparent hover:border-primary hover:bg-primary/5 transition-all outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                onClick={() => {
                                    onChoice('append')
                                    onOpenChange(false)
                                }}
                            >
                                <div className="p-3 bg-muted group-hover:bg-primary/10 rounded-full transition-colors">
                                    <FilePlus className="h-8 w-8 text-foreground group-hover:text-primary transition-colors" />
                                </div>
                                <div className="space-y-1 text-center">
                                    <span className="block font-semibold text-foreground">Append</span>
                                    <span className="block text-xs text-muted-foreground w-full px-2">
                                        Add the new outline to the end of the screenplay
                                    </span>
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Footer Section */}
                    <div className="p-6 pt-0 flex justify-end">
                        <Button variant="ghost" onClick={() => onOpenChange(false)}>
                            Cancel
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
